import { DOCUMENT } from "@angular/common";
import { Injectable, NgZone, inject } from "@angular/core";
import { Platform } from "@ionic/angular";
import { FirebaseDynamicLinks } from "@pantrist/capacitor-firebase-dynamic-links";
import { catchError, of } from "rxjs";

import { CampaignCodePrefillExperimentKeyService } from "src/app/experiments/campaign-code-prefill/campaign-code-prefill.service";
import { CampaignCodePrefillService } from "./campaign-code-prefill.service";
import { LoggerService } from "./logger.service";

interface RemovableListener {
  remove(): Promise<void> | void;
}

/**
 * Captures campaign-code candidates from web and Firebase Dynamic Link entry.
 */
@Injectable({ providedIn: "root" })
export class AppEntryCampaignCodeService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly platform = inject(Platform);
  private readonly experiment = inject(CampaignCodePrefillExperimentKeyService);
  private readonly campaignCodePrefill = inject(CampaignCodePrefillService);
  private readonly logger = inject(LoggerService);
  private started = false;
  private dynamicLinkEnabled = false;
  private listenerGeneration = 0;
  private dynamicLinkListener?: RemovableListener;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.campaignCodePrefill.captureFromUrlWhenEnabled(
      this.document.location?.href
    );

    this.experiment
      .valueWhenReady$()
      .pipe(
        catchError((error) => {
          this.logger.logError(error);
          return of(false);
        })
      )
      .subscribe((isEnabled) => this.setDynamicLinkEnabled(isEnabled));
  }

  private setDynamicLinkEnabled(isEnabled: boolean): void {
    if (isEnabled === this.dynamicLinkEnabled) {
      return;
    }

    this.dynamicLinkEnabled = isEnabled;
    if (isEnabled) {
      this.registerDynamicLinkListener();
    } else {
      this.removeDynamicLinkListener();
    }
  }

  private registerDynamicLinkListener(): void {
    const generation = ++this.listenerGeneration;
    void this.platform
      .ready()
      .then(() => {
        if (
          !this.dynamicLinkEnabled ||
          generation !== this.listenerGeneration
        ) {
          return;
        }

        try {
          this.dynamicLinkListener = FirebaseDynamicLinks.addListener(
            "deepLinkOpen",
            ({ url }) => {
              if (generation === this.listenerGeneration) {
                this.ngZone.run(() =>
                  this.campaignCodePrefill.captureFromUrlWhenEnabled(url)
                );
              }
            }
          );
        } catch (error) {
          this.logger.logError(error);
        }
      })
      .catch((error) => this.logger.logError(error));
  }

  private removeDynamicLinkListener(): void {
    this.listenerGeneration += 1;
    const listener = this.dynamicLinkListener;
    this.dynamicLinkListener = undefined;
    if (listener) {
      try {
        void Promise.resolve(listener.remove()).catch((error) =>
          this.logger.logError(error)
        );
      } catch (error) {
        this.logger.logError(error);
      }
    }
  }
}
